import { useState } from 'react';

/**
 * Meldet, ob sich die uebergebenen Werte seit dem letzten Render geaendert
 * haben. Beim ersten Render ist das Ergebnis immer `true`.
 *
 * Damit laesst sich abgeleiteter Zustand zuruecksetzen, ohne dafuer einen
 * Effekt zu missbrauchen — das von React empfohlene Muster „Zustand beim
 * Wechsel der Quelle zuruecksetzen":
 *
 * ```tsx
 * const quelleGewechselt = useSourceChanged([open, customer]);
 * if (quelleGewechselt && open) {
 *   setFormData(customer ? toFormData(customer) : leeresFormular);
 * }
 * ```
 *
 * Gegenueber einem Effekt entfaellt der zusaetzliche Render-Durchlauf, in dem
 * noch die alten Werte angezeigt wuerden: React verwirft das Render-Ergebnis
 * und rechnet direkt mit dem neuen Zustand weiter.
 *
 * Vergleich per `Object.is` — genau wie bei den Abhaengigkeiten eines Effekts.
 */
export function useSourceChanged(values: readonly unknown[]): boolean {
  const [previous, setPrevious] = useState<readonly unknown[] | null>(null);

  const changed =
    previous === null ||
    previous.length !== values.length ||
    values.some((value, index) => !Object.is(value, previous[index]));

  if (changed) {
    setPrevious(values);
  }

  return changed;
}

export default useSourceChanged;
